<template>
  <nav
    class="w-full shrink-0 self-start rounded-lg border border-gray-200 bg-white @7xl:w-72"
    :aria-label="t('title')"
    data-testid="step-list"
  >
    <div class="border-b border-gray-100 p-4">
      <h3 class="text-xl font-semibold text-gray-900">{{ t('title') }}</h3>
      <p class="mt-1 text-sm text-gray-600">{{ t('subtitle') }}</p>
      <p
        class="mt-3 text-xs font-light text-gray-900"
        data-testid="step-list-progress"
      >
        {{ t('progress', { done: completedCount, total: steps.length }) }}
      </p>
      <div
        class="mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-200"
        aria-hidden="true"
      >
        <div
          class="h-1.5 rounded-full bg-primary-500 transition-[width] duration-300 ease-out"
          :style="{ width: `${percent}%` }"
        />
      </div>
    </div>
    <ul class="flex flex-col p-2">
      <li
        v-for="row in rows"
        :key="row.id"
      >
        <button
          type="button"
          class="relative flex w-full items-center gap-2 rounded-md py-2.5 pl-3 pr-2 text-left focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary-700"
          :class="row.highlighted ? 'bg-primary-50' : 'hover:bg-gray-50'"
          :aria-current="row.active ? 'step' : null"
          :data-testid="`step-row-${row.id}`"
          :data-status="row.status"
          @click="$emit('open', row.id)"
        >
          <span
            v-if="row.active"
            class="absolute inset-y-1.5 left-0 w-1 rounded-full bg-blue-500"
            aria-hidden="true"
          />
          <CheckBurst
            v-if="row.justDone"
            class="size-6"
            :rings="[{ color: 'border-green-400' }]"
            data-testid="step-ring"
          >
            <ZoneIcon
              icon="check_circle"
              size="lg"
              class="text-green-400"
            />
          </CheckBurst>
          <span
            v-else
            class="flex size-6 shrink-0 items-center justify-center rounded-full"
            :class="{ 'bg-primary-100': row.highlighted }"
          >
            <ZoneIcon
              :icon="row.completed ? 'check_circle' : 'radio_button_unchecked'"
              size="lg"
              :class="row.completed ? 'text-green-400' : 'text-primary-500'"
              aria-hidden="true"
            />
          </span>
          <span
            class="flex-1 truncate text-sm"
            :class="row.titleClass"
          >{{ t(`step_names.${row.id}`) }}</span>
          <span class="sr-only">{{ row.completed ? t('completed') : t('not_completed') }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ZoneIcon from 'zone/components/base/Icon.vue';
import CheckBurst from '~/components/experiments/0239_getting_started_checklist/CheckBurst.vue';

const { steps, activeId = null, justDoneId = null } = defineProps({
  steps: { type: Array, required: true },
  activeId: { type: String },
  justDoneId: { type: String },
});

defineEmits(['open']);

const { t } = useI18n();

const completedCount = computed(() => steps.filter((step) => step.status === 'finished').length);
const percent = computed(() => Math.round((completedCount.value / steps.length) * 100));

const titleClass = (completed, active) => {
  if (completed) {return 'font-medium text-gray-900';}
  if (active) {return 'font-medium text-blue-500';}

  return 'font-regular text-gray-500';
};

const rows = computed(() => steps.map(({ id, status }) => {
  const completed = status === 'finished';
  const active = id === activeId;

  return {
    id,
    status,
    completed,
    active,
    highlighted: active && !completed,
    justDone: id === justDoneId,
    titleClass: titleClass(completed, active),
  };
}));
</script>


<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "title": "Get started",
    "subtitle": "Complete the tasks to get started",
    "progress": "{done}/{total} steps",
    "completed": "Completed",
    "not_completed": "Not completed"
  }
}
</i18n>
